import { EmptyState, FlexResponsive, FlexRow, Loading, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { Modal, ModalBody, ModalFooter } from 'baseui/modal';
import { LabelMedium } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFeedback } from '../../../../hooks/useFeedback';
import { useStyles } from '../../../../hooks/useStyles';
import { downloadRequest } from '../../../../services/api';
import { Asset, MinimalApiAsset } from '../../../../types/asset';
import { ChannelId } from '../../../../types/gptTypes';
import { StreamingCaptureChannelResponse } from '../../../../types/streaming';
import { downloadBlob } from '../../../../utils/files';
import useStreaming from '../hooks/useStreaming';
import StreamingCaptureHeader from './StreamingCaptureHeader';
import StreamingCapturePreview from './StreamingCapturePreview';
import StreamingFullScreenPreviewModal from './StreamingFullScreenPreviewModal';

type Props = {
  asset: Asset;
  onClose: () => void;
  isOpen: boolean;
};

function StreamingCaptureModal({ asset, onClose, isOpen }: Readonly<Props>) {
  const { theme, classes, css, concatenateClasses } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { loading: downloading, startLoading: startDownloading, stopLoading: stopDownloading } = useLoading();
  const [selectedSource, setSelectedSource] = useState<StreamingCaptureChannelResponse & { url: string }>();
  const [frontData, setFrontData] = useState<StreamingCaptureChannelResponse>();
  const [internalData, setInternalData] = useState<StreamingCaptureChannelResponse>();
  const [domeData, setDomeData] = useState<StreamingCaptureChannelResponse>();
  const [apiError, setApiError] = useState('');
  const { showFailFeedback } = useFeedback();
  const { getImagesData } = useStreaming();
  const hasLoaded = frontData !== undefined || internalData !== undefined;
  const hasAnyImage = !!frontData?.key || !!internalData?.key;

  useEffect(() => {
    if (isOpen && asset.client?._id && asset.subClient?._id) {
      loadImages({
        assetId: asset._id,
        clientId: asset.client._id,
        subClientId: asset.subClient._id,
      });
    }
  }, [asset._id, asset.client?._id, asset.subClient?._id, isOpen]);

  useEffect(() => {
    return () => {
      if (!isOpen) setApiError('');
    };
  }, [isOpen]);

  const loadImages = async (apiAsset: MinimalApiAsset) => {
    startLoading();
    const data = await getImagesData(apiAsset);
    if (!data?.error) {
      setFrontData(data?.frontData);
      setInternalData(data?.internalData);
      setDomeData(data?.domeData);
    } else {
      setApiError(typeof data.error === 'string' ? data.error : data.error.join(', '));
    }
    stopLoading();
  };

  const download = async () => {
    const sourcesToDownload = [frontData, internalData, domeData].filter((source) => !!source?.key && !!source.name);
    if (!sourcesToDownload.length) return;
    startDownloading();
    await Promise.all(
      sourcesToDownload.map(async (source) => {
        const { data, error } = await downloadRequest(source!.key!);
        if (error) {
          showFailFeedback(error);
        } else if (data) {
          downloadBlob(data.file, source!.name!);
        }
      }),
    );
    stopDownloading();
  };

  const closeFullScreen = () => {
    setSelectedSource(undefined);
  };

  return (
    <>
      {createPortal(
        <Modal
          animate
          closeable
          onClose={onClose}
          isOpen={isOpen}
          size="auto"
          overrides={{
            Dialog: {
              style: {
                minWidth: '450px',
                minHeight: '170px',
              },
            },
          }}
        >
          <StreamingCaptureHeader asset={asset} />
          <ModalBody>
            {hasLoaded && !loading && (
              <FlexResponsive centered>
                {internalData && (
                  <StreamingCapturePreview
                    data={internalData}
                    channelId={ChannelId.internal}
                    onPreview={(url) => setSelectedSource({ ...internalData, url })}
                  />
                )}
                {frontData && (
                  <StreamingCapturePreview
                    data={frontData}
                    channelId={ChannelId.front}
                    onPreview={(url) => setSelectedSource({ ...frontData, url })}
                  />
                )}
                {/* TODO: remove dome when channels < 3 */}
                {domeData && (
                  <StreamingCapturePreview
                    data={domeData}
                    channelId={ChannelId.dome}
                    onPreview={(url) => setSelectedSource({ ...domeData, url })}
                  />
                )}
              </FlexResponsive>
            )}
            {!hasLoaded && !loading && (
              <EmptyState style={{ minWidth: '400px' }}>
                <LabelMedium
                  maxWidth="350px"
                  color={theme.colors.negative}
                >
                  {apiError ? apiError : `Error: No se pudieron obtener las imágenes`}
                </LabelMedium>
              </EmptyState>
            )}
            {loading && <Loading size="compact" />}
          </ModalBody>
          {hasLoaded && !loading && (
            <ModalFooter
              className={css({
                marginLeft: '0 !important',
                marginRight: '0 !important',
                paddingLeft: theme.sizing.scale800,
                paddingRight: theme.sizing.scale800,
                borderTop: `${theme.borders.border200.borderWidth} ${theme.borders.border200.borderStyle} ${theme.borders.border200.borderColor}`,
              })}
            >
              <FlexRow
                gap={theme.sizing.scale300}
                classNames={concatenateClasses(classes.centeredEnd, css({ maxWidth: '300px', margin: '0 auto' }))}
              >
                <StyledButton
                  kind="secondary"
                  onClick={onClose}
                  full
                >
                  Cerrar
                </StyledButton>
                <StyledButton
                  onClick={download}
                  isLoading={downloading}
                  disabled={!hasAnyImage}
                  full
                >
                  Descargar fotos
                </StyledButton>
              </FlexRow>
            </ModalFooter>
          )}
        </Modal>,
        document.body,
      )}
      {selectedSource && (
        <StreamingFullScreenPreviewModal
          asset={asset}
          isOpen
          onClose={closeFullScreen}
          source={selectedSource}
        />
      )}
    </>
  );
}

export default memo(StreamingCaptureModal);
